import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  KingBed as KingBedIcon,
  ContactPage as ContactIcon,
  Info as InfoIcon,
  Collections as GalleryIcon,
} from '@mui/icons-material'
import { motion, AnimatePresence } from 'framer-motion'
import Logo from './Logo'

const MotionMenuItem = motion(MenuItem)
const MotionBox = motion(Box)

const pages = [
  { title: 'Home', path: '/', icon: <HomeIcon /> },
  { title: 'Rooms', path: '/rooms', icon: <KingBedIcon /> },
  { title: 'About', path: '/about', icon: <InfoIcon /> },
  { title: 'Gallery', path: '/gallery', icon: <GalleryIcon /> },
  { title: 'Contact', path: '/contact', icon: <ContactIcon /> },
]

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    handleCloseNavMenu()
  }

  const NavButton = ({ page, index }: { page: typeof pages[0], index: number }) => {
    const isActive = location.pathname === page.path
    
    return (
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.3,
          delay: index * 0.1,
          ease: "easeOut"
        }}
      >
        <Button
          onClick={() => handleNavigate(page.path)}
          sx={{
            position: 'relative',
            my: 2,
            mx: 1,
            color: isActive ? 'primary.main' : 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.95rem',
            fontWeight: isActive ? 600 : 500,
            textTransform: 'none',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: isActive ? 0 : '50%',
              right: isActive ? 0 : '50%',
              height: '2px',
              bgcolor: 'primary.main',
              transition: 'all 0.3s ease',
            },
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              '&::before': {
                left: '0',
                right: '0',
              },
            },
          }}
        >
          <MotionBox
            component={motion.div}
            whileHover={{ scale: 1.2, rotate: 5 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: isActive ? 'primary.main' : 'inherit',
              '& > svg': {
                fontSize: '1.2rem',
              },
            }}
          >
            {page.icon}
          </MotionBox>
          {page.title}
        </Button>
      </MotionBox>
    )
  }

  return (
    <AppBar 
      position="fixed" 
      color="transparent"
      sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        top: '40px',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar 
          disableGutters
          sx={{
            minHeight: { xs: '64px', md: '72px' },
            py: 1,
          }}
        >
          {/* Logo/Brand - Always visible */}
          <MotionBox
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              mr: 4,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Logo />
          </MotionBox>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiPaper-root': {
                  borderRadius: 2,
                  mt: 1,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  backdropFilter: 'blur(8px)',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                },
              }}
            >
              <AnimatePresence>
                {pages.map((page, index) => (
                  <MotionMenuItem
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    sx={{
                      my: 0.5,
                      mx: 1,
                      borderRadius: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Stack 
                      direction="row" 
                      spacing={1.5} 
                      alignItems="center"
                      sx={{
                        color: location.pathname === page.path ? 'primary.main' : 'text.primary',
                      }}
                    >
                      <MotionBox
                        component={motion.div}
                        whileHover={{ scale: 1.2, rotate: 5 }}
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          '& > svg': {
                            fontSize: '1.2rem',
                          },
                        }}
                      >
                        {page.icon}
                      </MotionBox>
                      <Typography 
                        sx={{ 
                          fontWeight: location.pathname === page.path ? 600 : 400,
                        }}
                      >
                        {page.title}
                      </Typography>
                    </Stack>
                  </MotionMenuItem>
                ))}
              </AnimatePresence>
            </Menu>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'center' }}>
            {pages.map((page, index) => (
              <NavButton key={page.path} page={page} index={index} />
            ))}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default Navbar 